/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Platform API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  EmailCheckResult,
  GetPlatformV1AuditLogs200,
  GetPlatformV1AuditLogsParams,
  GetPlatformV1OpsExchangeRates200,
  OpsOverview,
  PlatformEmailSettings,
  PostPlatformV1OpsEmailTestBody,
  PutPlatformV1OpsExchangeRates200,
  PutPlatformV1OpsExchangeRatesBody,
  ReindexAllResult
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Which email driver is configured and the sender address
 */
export const getPlatformV1OpsEmail = (

 options?: SecondParameter<typeof customInstance<PlatformEmailSettings>>,) => {
      return customInstance<PlatformEmailSettings>(
      {url: `/platform/v1/ops/email`, method: 'GET'
    },
      options);
    }
  /**
 * Connects and authenticates to the SMTP server (the log driver always passes). With `to`, also delivers a short test message there
 * @summary Check the outbound email settings and optionally send a test message
 */
export const postPlatformV1OpsEmailTest = (
    postPlatformV1OpsEmailTestBody: BodyType<PostPlatformV1OpsEmailTestBody>,
 options?: SecondParameter<typeof customInstance<EmailCheckResult>>,) => {
      return customInstance<EmailCheckResult>(
      {url: `/platform/v1/ops/email/test`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postPlatformV1OpsEmailTestBody
    },
      options);
    }
  /**
 * Rows from every organization plus platform-level actions (operator logins, applications, provisioning). Filter by organization, actor type and id, action, entity, store and time
 * @summary The platform-wide audit log, newest first
 */
export const getPlatformV1AuditLogs = (
    params?: GetPlatformV1AuditLogsParams,
 options?: SecondParameter<typeof customInstance<GetPlatformV1AuditLogs200>>,) => {
      return customInstance<GetPlatformV1AuditLogs200>(
      {url: `/platform/v1/audit-logs`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Queues, outbox lag, webhook failures, search index drift and rollup freshness
 */
export const getPlatformV1OpsOverview = (

 options?: SecondParameter<typeof customInstance<OpsOverview>>,) => {
      return customInstance<OpsOverview>(
      {url: `/platform/v1/ops/overview`, method: 'GET'
    },
      options);
    }
  /**
 * The worker rebuilds one index per store; this answers as soon as the jobs are queued
 * @summary Queue a full search reindex of every active and paused store
 */
export const postPlatformV1OpsSearchReindex = (

 options?: SecondParameter<typeof customInstance<ReindexAllResult>>,) => {
      return customInstance<ReindexAllResult>(
      {url: `/platform/v1/ops/search/reindex`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Latest exchange rate per currency pair
 */
export const getPlatformV1OpsExchangeRates = (

 options?: SecondParameter<typeof customInstance<GetPlatformV1OpsExchangeRates200>>,) => {
      return customInstance<GetPlatformV1OpsExchangeRates200>(
      {url: `/platform/v1/ops/exchange-rates`, method: 'GET'
    },
      options);
    }
  /**
 * Each rate is units of quote per one unit of base. Rows are appended, so history is kept; stores pick the newest per pair. A daily provider fetch will use the same table
 * @summary Record exchange rates by hand
 */
export const putPlatformV1OpsExchangeRates = (
    putPlatformV1OpsExchangeRatesBody: BodyType<PutPlatformV1OpsExchangeRatesBody>,
 options?: SecondParameter<typeof customInstance<PutPlatformV1OpsExchangeRates200>>,) => {
      return customInstance<PutPlatformV1OpsExchangeRates200>(
      {url: `/platform/v1/ops/exchange-rates`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putPlatformV1OpsExchangeRatesBody
    },
      options);
    }
  export type GetPlatformV1OpsEmailResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OpsEmail>>>
export type PostPlatformV1OpsEmailTestResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OpsEmailTest>>>
export type GetPlatformV1AuditLogsResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1AuditLogs>>>
export type GetPlatformV1OpsOverviewResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OpsOverview>>>
export type PostPlatformV1OpsSearchReindexResult = NonNullable<Awaited<ReturnType<typeof postPlatformV1OpsSearchReindex>>>
export type GetPlatformV1OpsExchangeRatesResult = NonNullable<Awaited<ReturnType<typeof getPlatformV1OpsExchangeRates>>>
export type PutPlatformV1OpsExchangeRatesResult = NonNullable<Awaited<ReturnType<typeof putPlatformV1OpsExchangeRates>>>
